import React, { useState } from "react";
import { ArrowLeft, Landmark, IdCard, FileText, MoreHorizontal, Trash2, Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientDocumentsPanel } from "@/components/admin/ClientDocumentsPanel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AdminTransactionForm } from "@/components/admin/AdminTransactionForm";
import { api } from "@/lib/api";

function Field({ label, value, mono }) {
  return (<div><dt className="tech-label text-muted-foreground">{label}</dt><dd className={`mt-1 text-sm text-foreground ${mono ? "font-mono" : ""}`}>{value}</dd></div>);
}

export function ClientDetail({ client, transactions, onBack, onChanged }) {
  const { t, lang } = useLanguage();
  const [showTxForm, setShowTxForm] = useState(false);
  const fmtMoney = (a, c) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: c }).format(Number(a));
  const idTypeLabel = (v) => (v === "passport" ? t("admin.idType.passport") : v === "residence" ? t("admin.idType.residence") : t("admin.idType.id"));

  const setStatus = async (id, status) => { await api.patch(`/admin/transactions/${id}/status`, { status }); onChanged(); };
  const del = async (id) => { await api.delete(`/admin/transactions/${id}`); onChanged(); };

  return (
    <div>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"><ArrowLeft className="h-4 w-4" />{t("admin.detail.back")}</button>
      <div className="mt-4">
        <h1 className="font-serif text-2xl font-semibold tracking-tight text-foreground">{client.name}</h1>
        <p className="text-sm text-muted-foreground">{client.institutionName} · {client.email}</p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card className="premium-card overflow-hidden p-0">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4"><Landmark className="h-5 w-5 text-accent" /><h2 className="font-medium text-foreground">{t("admin.detail.bankTitle")}</h2></div>
          {client.account ? (
            <div className="px-5 py-5"><dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label={t("admin.form.accountHolder")} value={client.account.accountHolder} />
              <Field label={t("admin.form.bankName")} value={client.account.bankName} />
              <Field label={t("admin.form.iban")} value={client.account.iban} mono />
              <Field label={t("admin.form.bic")} value={client.account.bic ?? "—"} mono />
            </dl></div>
          ) : <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t("admin.noBank")}</p>}
        </Card>

        <Card className="premium-card overflow-hidden p-0">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4"><IdCard className="h-5 w-5 text-accent" /><h2 className="font-medium text-foreground">{t("admin.detail.identityTitle")}</h2></div>
          {client.identity ? (
            <div className="px-5 py-5"><dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label={t("admin.form.idFullName")} value={client.identity.fullName} />
              <Field label={t("admin.form.idType")} value={idTypeLabel(client.identity.idType)} />
              <Field label={t("admin.form.idNumber")} value={client.identity.idNumber} mono />
              <Field label={t("admin.form.birthDate")} value={client.identity.birthDate} />
              <Field label={t("admin.form.nationality")} value={client.identity.nationality} />
            </dl></div>
          ) : <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t("admin.detail.noIdentity")}</p>}
        </Card>
      </div>

      <Card className="premium-card mt-6 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-medium text-foreground">{t("admin.detail.txTitle")}</h2>
          <Button size="sm" onClick={() => setShowTxForm((v) => !v)}><Plus className="h-4 w-4" />{t("admin.detail.newTx")}</Button>
        </div>
        {showTxForm && <div className="border-b border-border p-5"><AdminTransactionForm clientId={client.id} clientName={client.name} onDone={() => { setShowTxForm(false); onChanged(); }} /></div>}
        {transactions.length === 0 ? <p className="px-5 py-10 text-center text-sm text-muted-foreground">{t("section.empty")}</p> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>{t("portal.reference")}</TableHead><TableHead>{t("portal.investor")}</TableHead><TableHead className="text-right">{t("portal.amount")}</TableHead><TableHead>{t("portal.status")}</TableHead><TableHead className="text-right">{t("portal.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell><span className="font-mono text-xs font-medium text-foreground">{tx.reference}</span></TableCell>
                  <TableCell className="text-foreground">{tx.investorName}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-foreground">{fmtMoney(tx.amount, tx.currency)}</TableCell>
                  <TableCell><StatusBadge status={tx.status} /></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {tx.status === "sent" && <DropdownMenuItem onClick={() => setStatus(tx.id, "received")}>{t("admin.action.confirmReceived")}</DropdownMenuItem>}
                        {tx.status === "received" && <DropdownMenuItem onClick={() => setStatus(tx.id, "released")}>{t("admin.action.release")}</DropdownMenuItem>}
                        {tx.status === "pending" && <DropdownMenuItem onClick={() => setStatus(tx.id, "received")}>{t("admin.action.confirmReceived")}</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => del(tx.id)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />{t("portal.action.delete")}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        )}
      </Card>

      <Card className="premium-card mt-6 overflow-hidden p-0">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-4"><FileText className="h-5 w-5 text-accent" /><h2 className="font-medium text-foreground">{t("admin.docs.title")}</h2></div>
        <ClientDocumentsPanel clientId={client.id} />
      </Card>
    </div>
  );
}
